import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

const TelemedicineSessionModal = ({
  isOpen,
  onClose,
  notificationData,
  isLoading = false
}) => {
  const navigate = useNavigate()
  const [selectedService, setSelectedService] = useState(null)
  const [medicalServices, setMedicalServices] = useState([])
  const [servicesLoading, setServicesLoading] = useState(false)
  const [servicesError, setServicesError] = useState(null)
  let { clinic_id } = useParams();

  // Fetch medical services from API
  const fetchMedicalServices = async () => {
    const userData = localStorage.getItem("user_data");
    if (userData && userData !== "undefined" && userData !== "null") {
      const userDataParsed = JSON.parse(userData);
      clinic_id = userDataParsed.clinic_id;
    }

    if (!clinic_id) return

    try {
      setServicesLoading(true)
      setServicesError(null)

      const token = localStorage.getItem('access_token')
      const response = await fetch(
        `${API_BASE_URL}/api/v1/clinics/services`,
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch services: ${response.status}`)
      }

      const result = await response.json()

      // Handle different response structures
      let services = []
      if (result.data && Array.isArray(result.data)) {
        services = result.data
      } else if (result.data && Array.isArray(result.data.services)) {
        services = result.data.services
      } else if (Array.isArray(result)) {
        services = result
      }

      // Filter for medical services and transform to match expected format
      const medicalServicesData = services
        .filter(service => service.service_type === 'medical' || service.type === 'medical')
        .map(service => ({
          id: service.id || service.service_id,
          name: service.service_name_en || service.name || service.service_name,
          price: service.final_price || service.price || 0,
          icon: getServiceIcon(service.service_name_en || service.name || service.service_name),
          description: service.description || service.service_description || 'Medical consultation service',
          originalData: service
        }))

      setMedicalServices(medicalServicesData)
    } catch (error) {
      console.error('Error fetching medical services:', error)
      setServicesError(error.message)
      // Fallback to default services if API fails
      setMedicalServices(getDefaultServices())
    } finally {
      setServicesLoading(false)
    }
  }

  // Get appropriate icon for service
  const getServiceIcon = (serviceName) => {
    const name = serviceName?.toLowerCase() || ''
    if (name.includes('general') || name.includes('consultation')) return '🩺'
    if (name.includes('skin') || name.includes('dermat')) return '🐕'
    if (name.includes('ear') || name.includes('otitis')) return '👂'
    if (name.includes('eye') || name.includes('ophthalm')) return '👁️'
    if (name.includes('gastro') || name.includes('digest')) return '🤢'
    if (name.includes('follow') || name.includes('check')) return '📋'
    if (name.includes('vaccin') || name.includes('immun')) return '💉'
    if (name.includes('surgery') || name.includes('surgical')) return '🏥'
    if (name.includes('dental') || name.includes('tooth')) return '🦷'
    if (name.includes('emergency') || name.includes('urgent')) return '🚨'
    return '🩺' // Default medical icon
  }



  // Handle modal close
  const handleClose = () => {
    if (!isLoading) {
      setSelectedService(null)
      onClose()
    }
  }

  // Handle backdrop click - disabled to prevent accidental dismissal
  const handleBackdropClick = (e) => {
    // Modal is not dismissable by clicking backdrop
    e.preventDefault()
    e.stopPropagation()
  }

  // Handle escape key - disabled to prevent accidental dismissal
  const handleKeyDown = (e) => {
    // Modal is not dismissable by escape key
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  // Handle service selection
  const handleServiceSelect = (service) => {
    setSelectedService(service)
  }

  // Handle continue action
  const handleContinue = () => {
    if (selectedService && notificationData) {
      // Here you would typically start the telemedicine session


      // Navigate to telemedicine session or close modal
      // navigate(`/telemedicine/session/${notificationData.content_id}?service=${selectedService.id}`)
      handleClose()
    }
  }

  useEffect(() => {
    if (isOpen) {
      // Note: No escape key listener added - modal is not dismissable by escape key
      document.body.style.overflow = 'hidden'
      // Fetch medical services when modal opens
      fetchMedicalServices()
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, isLoading])

  if (!isOpen || !notificationData) return null

  // Extract data from notification
  const { pet_data = [] } = notificationData
  const primaryPet = pet_data && pet_data.length > 0 ? pet_data[0] : null
  const petName = primaryPet?.name || 'Unknown Pet'

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          borderColor: '#00D4B3',
          borderWidth: '3px',
          borderStyle: 'solid',
          padding: '32px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          transform: isOpen ? 'scale(1)' : 'scale(0.95)',
          transition: 'transform 0.2s ease-out',
          fontFamily: '"Red Hat Display", sans-serif'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '8px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#00D4B3',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '600',
              color: '#00D4B3',
              margin: 0,
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              Telemedicine Session
            </h2>
          </div>
          <p style={{
            fontSize: '16px',
            color: '#6B7280',
            margin: 0,
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            Start a virtual consultation with a Pet Guardian
          </p>
        </div>

        {/* Initiate Session Section */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#1F2937',
            margin: '0 0 16px 0',
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            Initiate Session
          </h3>

          {/* Pet Information Card */}
          <div style={{
            backgroundColor: '#F9FAFB',
            border: '2px solid #E5E7EB',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            {/* Pet Image */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: '#E5E7EB',
              flexShrink: 0
            }}>
              {primaryPet?.image ? (
                <img
                  src={primaryPet.image}
                  alt={petName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#00D4B3'
                }}>
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              )}
            </div>

            {/* Pet Details */}
            <div style={{ flex: 1 }}>
              <h4 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1F2937',
                margin: '0 0 8px 0',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {petName}
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '8px',
                fontSize: '14px',
                color: '#6B7280',
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {primaryPet?.species && (
                  <div>
                    <span style={{ fontWeight: '500' }}>Species:</span> {primaryPet.species}
                  </div>
                )}
                {primaryPet?.age !== undefined && primaryPet.age > 0 && (
                  <div>
                    <span style={{ fontWeight: '500' }}>Age:</span> {primaryPet.age} {primaryPet.age === 1 ? 'year' : 'years'}
                  </div>
                )}
                {primaryPet?.breed && (
                  <div>
                    <span style={{ fontWeight: '500' }}>Breed:</span> {primaryPet.breed}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Medical Service Section */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#1F2937',
            margin: '0 0 16px 0',
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            Medical Service
          </h3>

          {/* Loading State */}
          {servicesLoading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              backgroundColor: '#F9FAFB',
              borderRadius: '12px',
              border: '2px solid #E5E7EB'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #E5E7EB',
                  borderTop: '3px solid #00D4B3',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  margin: 0,
                  fontFamily: '"Red Hat Display", sans-serif'
                }}>
                  Loading medical services...
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {servicesError && !servicesLoading && (
            <div style={{
              padding: '20px',
              backgroundColor: '#FEF2F2',
              borderRadius: '12px',
              border: '2px solid #FECACA',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#EF4444',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  !
                </div>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#DC2626',
                  margin: 0,
                  fontFamily: '"Red Hat Display", sans-serif'
                }}>
                  Failed to load services
                </h4>
              </div>
              <p style={{
                fontSize: '14px',
                color: '#7F1D1D',
                margin: 0,
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {servicesError}. Using default services.
              </p>
            </div>
          )}

          {/* Services Grid */}
          {!servicesLoading && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              {medicalServices.length === 0 ? (
                <div style={{
                  gridColumn: '1 / -1',
                  padding: '40px',
                  textAlign: 'center',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB'
                }}>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    🩺
                  </div>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 8px 0',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    No Medical Services Available
                  </h4>
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: 0,
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}>
                    No medical services are currently available for this clinic.
                  </p>
                </div>
              ) : (
                medicalServices.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => handleServiceSelect(service)}
                    style={{
                      backgroundColor: selectedService?.id === service.id ? '#F0FDF4' : '#FFFFFF',
                      border: selectedService?.id === service.id ? '2px solid #00D4B3' : '2px solid #E5E7EB',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontFamily: '"Red Hat Display", sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedService?.id !== service.id) {
                        e.target.style.borderColor = '#00D4B3'
                        e.target.style.backgroundColor = '#F9FFFE'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedService?.id !== service.id) {
                        e.target.style.borderColor = '#E5E7EB'
                        e.target.style.backgroundColor = '#FFFFFF'
                      }
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        fontSize: '24px',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: selectedService?.id === service.id ? '#00D4B3' : '#F3F4F6',
                        borderRadius: '8px'
                      }}>
                        {service.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1F2937',
                          margin: '0 0 4px 0',
                          fontFamily: '"Red Hat Display", sans-serif'
                        }}>
                          {service.name}
                        </h4>
                        <p style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          margin: 0,
                          fontFamily: '"Red Hat Display", sans-serif'
                        }}>
                          {service.id}
                        </p>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#00D4B3',
                        fontFamily: '"Red Hat Display", sans-serif'
                      }}>
                        ${service.price}
                      </span>
                      {selectedService?.id === service.id && (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor: '#00D4B3',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center'
        }}>
          {/* <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            style={{
              padding: '12px 24px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#374151',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: '"Red Hat Display", sans-serif',
              opacity: isLoading ? 0.6 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#F9FAFB'
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = 'white'
              }
            }}
          >
            Cancel
          </button> */}
          <button
            type="button"
            onClick={handleContinue}
            disabled={isLoading || !selectedService}
            style={{
              padding: '12px 24px',
              border: 'none',
              width: '50%',
              borderRadius: '8px',
              backgroundColor: isLoading || !selectedService ? '#D1D5DB' : '#00D4B3',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading || !selectedService ? 'not-allowed' : 'pointer',
              fontFamily: '"Red Hat Display", sans-serif',
              transition: 'all 0.2s ease',
              opacity: isLoading || !selectedService ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading && selectedService) {
                e.target.style.backgroundColor = '#00BFA5'
                e.target.style.opacity = '0.9'
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && selectedService) {
                e.target.style.backgroundColor = '#00D4B3'
                e.target.style.opacity = '1'
              }
            }}
          >
            {isLoading ? 'Starting...' : 'Continue'}
          </button>
        </div>
      </div>

      {/* CSS Animation for Spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default TelemedicineSessionModal
